import { hm } from './dates';
import { mean } from './format';
import { logDate } from './logs';

// Bowel movement rules. Bristol 3–4 is normal, 1–2 hard, 5–7 loose. Urgency
// and straining are 0–3 (nullable floats): averages use the logged value and
// skip nulls; level buckets (No / A little / Some / A lot) round to the
// nearest whole number. `evacuation_complete` null is unknown — left out of
// "complete evacuation" and of the incomplete count alike.

/**
 * The Bristol type 1–7 of a row: the number in `bristol_stool_name`
 * ("Type 4"), else its id looked up in the profile utils.
 * @param {(id: any) => number|null} [lookup]
 */
export function bristolType(row, lookup) {
  const m = /([1-7])/.exec(String(row?.bristol_stool_name ?? ''));
  if (m) return Number(m[1]);
  const t = lookup ? Number(lookup(row?.bristol_stool_id)) : NaN;
  return t >= 1 && t <= 7 ? t : null;
}

/**
 * Id → type lookup over `profileUtils.bristol_stool_types`. Its row shape
 * isn't confirmed yet, so the type is read from a numeric field or the name.
 */
export function bristolLookup(types) {
  if (!Array.isArray(types)) return () => null;
  const byId = new Map();
  types.forEach((t) => {
    if (!t || t.id == null) return;
    const label = String(t.name ?? t.title ?? t.label ?? t.description ?? '');
    const n = Number(t.type ?? t.value ?? t.number ?? /([1-7])/.exec(label)?.[1]);
    if (n >= 1 && n <= 7) byId.set(String(t.id), n);
  });
  return (id) => byId.get(String(id)) ?? null;
}

export const category = (ty) => (ty == null ? null : ty <= 2 ? 'Hard' : ty <= 4 ? 'Normal' : 'Loose');
export const isNormal = (ty) => ty === 3 || ty === 4;

/** A 0–3 rating's level (0 No … 3 A lot), or null when not recorded. */
export function level(v) {
  if (v == null || !Number.isFinite(Number(v))) return null;
  return Math.max(0, Math.min(3, Math.round(Number(v))));
}

const rating = (v) => (v == null || !Number.isFinite(Number(v)) ? null : Number(v));

/** Raw bowel rows → movements by local day, each day in time order. */
export function bowelDays(rows = [], lookup) {
  const days = {};
  rows.forEach((r) => {
    const iso = logDate(r);
    if (!iso) return;
    (days[iso] || (days[iso] = [])).push({
      day: iso,
      time: hm(r.date),
      ty: bristolType(r, lookup),
      u: rating(r.urgency_rating),
      s: rating(r.strain_rating),
      complete: typeof r.evacuation_complete === 'boolean' ? r.evacuation_complete : null,
    });
  });
  Object.values(days).forEach((list) => list.sort((a, b) => a.time.localeCompare(b.time)));
  return days;
}

/** Movements at each level 0–3 for `key` ('u' or 's'), over movements with that rating. */
export function levelCounts(M, key) {
  const c = [0, 0, 0, 0];
  M.forEach((m) => {
    const l = level(m[key]);
    if (l != null) c[l] += 1;
  });
  return c;
}

const share = (k, n) => (n ? (k / n) * 100 : null);

/**
 * Stats over a list of movements.
 * - `normal`: % of typed movements in Bristol 3–4.
 * - `complete`: % complete, of movements whose evacuation is known.
 * - `comfort`: mean of (3 − urgency)/3·100, (3 − straining)/3·100 and
 *   `complete` — over the parts that have data.
 */
export function bowelStats(M) {
  const typed = M.filter((m) => m.ty != null);
  const known = M.filter((m) => m.complete != null);
  const nc = known.filter((m) => m.complete).length;
  const urg = mean(M.map((m) => m.u));
  const str = mean(M.map((m) => m.s));
  const complete = share(nc, known.length);
  const types = [1, 2, 3, 4, 5, 6, 7].map((ty) => typed.filter((m) => m.ty === ty).length);
  const good = types[2] + types[3];
  const uCounts = levelCounts(M, 'u');
  const sCounts = levelCounts(M, 's');
  const total = (c) => c.reduce((a, b) => a + b, 0);
  return {
    M,
    n: M.length,
    typed: typed.length,
    types,
    good,
    hard: types[0] + types[1],
    loose: types[4] + types[5] + types[6],
    normal: share(good, typed.length),
    urg,
    str,
    known: known.length,
    nc,
    inc: known.length - nc,
    complete,
    comfort: mean([urg == null ? null : ((3 - urg) / 3) * 100, str == null ? null : ((3 - str) / 3) * 100, complete]),
    uCounts,
    sCounts,
    noUrg: share(uCounts[0], total(uCounts)),
    noStr: share(sCounts[0], total(sCounts)),
  };
}

/** Movements on the given days. */
export const movementsOn = (days, isos) => isos.flatMap((iso) => days[iso] || []);
